import { DayLight, NTPSyncEnable, TimeMode } from '@EcamModel/model/PMC8266SysDateTime';
import { Divider, Stack, Typography } from '@mui/material';
import { useCameraDetailContext } from '@pages/cameras/hook/CameraDetailsProvider';
import { SectionsCollapsePMC8266 } from '@pages/cameras/hook/useMilesightPMC8266';
import { useEffect, useState } from 'react';
import { formatUKDateDash, formatUKTime, getTimezoneDescription } from 'src/helpers';
import {
    DataSectionHasSubItem,
    SectionHasSubItemHeader,
    SectionNotSubItem,
} from '../../../ptz/milesight/components/BasicInfoMilesightCameraPTZ';

const dayLightMapping: { [key in DayLight]: string } = {
    [DayLight.Disabled]: 'Disabled',
    [DayLight.Automatic]: 'Automatic',
};

const timeTypeMapping: { [key in TimeMode]: string } = {
    [TimeMode.NTP]: 'NTP server',
    [TimeMode.Computer]: 'Synchronize with computer time',
    [TimeMode.Manual]: 'Manual',
    [TimeMode.GPS]: 'GPS',
};

const ntpSyncEnableMapping: { [key in NTPSyncEnable]: string } = {
    [NTPSyncEnable.Disable]: 'Disable',
    [NTPSyncEnable.Enable]: 'Enable',
};

const ntpIntervalMapping: { [key: number]: string } = {
    60: '1 hour',
    120: '2 hours',
    300: '5 hours',
    600: '10 hours',
    1440: '1 day',
    2880: '2 days',
    7200: '5 days',
    14400: '10 days',
    43200: '30 days',
};

type Props = {
    isOpenCollapse: { [key in SectionsCollapsePMC8266]: boolean };
    handleToggleCollapse: (section: SectionsCollapsePMC8266) => void;
};

export default function DateAndTimeMilesightsPMC8266(props: Props) {
    const { camera } = useCameraDetailContext();
    const { isOpenCollapse, handleToggleCollapse } = props;
    const [time, setTime] = useState(formatUKTime(new Date()));

    const PMC8266SysDateTime = camera?.PMC8266Camera?.PMC8266CameraConfigs?.SysDateTime;

    const statusList_CurrentSystemTime: DataSectionHasSubItem[] = [
        {
            label: 'Date:',
            value: <Typography>{formatUKDateDash(new Date())}</Typography>,
        },
        {
            label: 'Time:',
            value: <Typography>{time}</Typography>,
        },
    ];

    const statusList_SetTheSystemTime: DataSectionHasSubItem[] = [
        {
            label: 'Time zone:',
            value: <Typography>{getTimezoneDescription(PMC8266SysDateTime?.TimeZoneTz!)}</Typography>,
        },
        {
            label: 'Daylight saving time:',
            value: <Typography>{dayLightMapping[PMC8266SysDateTime?.DayLight!]}</Typography>,
        },
        {
            label: 'Synchronize mode:',
            value: <Typography>{timeTypeMapping[PMC8266SysDateTime?.TimeType!]}</Typography>,
        },
        {
            label: 'Server address:',
            value: <Typography>{PMC8266SysDateTime?.NtpServer!}</Typography>,
        },
        {
            label: 'NTP sync:',
            value: <Typography>{ntpSyncEnableMapping[PMC8266SysDateTime?.NtpSyncEnable!]}</Typography>,
        },
        {
            label: 'Interval:',
            value: (
                <Typography>
                    {ntpIntervalMapping[PMC8266SysDateTime?.NtpInterval!] ||
                        `${PMC8266SysDateTime?.NtpInterval} minutes`}
                </Typography>
            ),
        },
    ];

    const handleEdit = () => {
        console.log('Edit button clicked');
    };

    useEffect(() => {
        const intervalId = setInterval(() => {
            setTime(formatUKTime(new Date()));
        }, 1000);

        return () => clearInterval(intervalId);
    }, []);

    return (
        <Stack id="set_the_systemTime" border={'1px solid #E4E7EC'} borderRadius={2} flex={1}>
            <SectionHasSubItemHeader id="" title="Date & Time" onEdit={handleEdit} />

            <Divider
                orientation="vertical"
                sx={{
                    borderBottomWidth: 1,
                    borderColor: '#E4E7EC',
                    mx: 2,
                    mb: 2,
                }}
            />

            <SectionNotSubItem
                header="Set the System Time"
                statusList={statusList_SetTheSystemTime}
                isOpen={isOpenCollapse.set_the_systemTime}
                onToggleCollapse={() => {
                    handleToggleCollapse(SectionsCollapsePMC8266.SetTheSystemTime);
                }}
            />

            <SectionNotSubItem
                name="current_systemTime"
                header="Current system time"
                statusList={statusList_CurrentSystemTime}
                isOpen={isOpenCollapse.current_systemTime}
                onToggleCollapse={() => {
                    handleToggleCollapse(SectionsCollapsePMC8266.CurrentSystemTime);
                }}
            />
        </Stack>
    );
}
